import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { InfoCircle, InfoCircleSmall } from '@wix/wix-ui-icons-common';
import { TooltipCommonProps } from '../common/PropTypes/TooltipCommon';
import { dataHooks } from './constants';

import Tooltip, { TooltipActions } from '../Tooltip';
import { st, classes } from './InfoIcon.st.css';
import { InfoIconProps } from './InfoIcon.types';
import { useId } from '../utils/useId';

const iconComponentBySizeMap = {
  small: InfoCircleSmall,
  medium: InfoCircle,
};

const InfoIcon = ({
  dataHook,
  content,
  size = 'medium',
  tooltipProps,
  className,
  children,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: InfoIconProps) => {
  const Icon = iconComponentBySizeMap[size];
  const tooltipRef = useRef<TooltipActions>(null);

  const autoId = useId();
  const propAriaLabelledBy =
    (tooltipProps as any)?.['aria-labelledby'] ?? ariaLabelledBy;
  const propAriaDescribedBy =
    (tooltipProps as any)?.['aria-describedby'] ?? ariaDescribedBy;
  const tooltipId =
    propAriaLabelledBy || propAriaDescribedBy || `infoicon-tooltip-${autoId}`;

  const mergedTooltipProps = {
    ...tooltipProps,
    ...(propAriaLabelledBy
      ? { 'aria-labelledby': propAriaLabelledBy }
      : { 'aria-describedby': propAriaDescribedBy || tooltipId }),
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      tooltipRef.current?.close();
    }
  };

  return (
    <div
      className={st(classes.root, className)}
      data-size={size}
      data-hook={dataHook}
    >
      <Tooltip
        {...mergedTooltipProps}
        content={content}
        dataHook={dataHooks.tooltip}
        ref={tooltipRef}
      >
        <div
          role="button"
          tabIndex={0}
          aria-haspopup={true}
          aria-controls={tooltipId}
          onKeyDown={handleKeyDown}
        >
          {children ?? <Icon className={classes.icon} />}
        </div>
      </Tooltip>
    </div>
  );
};

InfoIcon.displayName = 'InfoIcon';

InfoIcon.propTypes = {
  dataHook: PropTypes.string,
  className: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium']),
  content: PropTypes.node.isRequired,
  tooltipProps: PropTypes.shape(TooltipCommonProps),
  children: PropTypes.node,
  'aria-describedby': PropTypes.string,
  'aria-labelledby': PropTypes.string,
};

export default InfoIcon;
